import React from 'react';
import { Route, Redirect, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

function AdminPrivateRoute(props) {
    const Component = props.component;
    const clientname = props.clientname;
    const accountType = props.accountType;

    // Everything else is forwarded to <Route>, MINUS the props injected by the
    // two wrappers below. withRouter adds match/location/history/staticContext
    // and connect adds dispatch; none of them belong on a <Route>, and
    // `location` in particular has its own meaning there (it makes the Route
    // match against that location instead of the context one).
    const rest = Object.assign({}, props);
    ['component', 'clientname', 'accountType', 'match', 'location', 'history',
        'staticContext', 'dispatch'].forEach(function (key) {
        delete rest[key];
    });

    let pathname = '/admin/login';

    if (window.document.location.pathname.indexOf('customerdashboard') > -1)
        pathname = '/customerdashboard/login';

    return (
        <Route
            {...rest}
            render = {props => {
                if (isLogged() !== 'true') {
                    return (
                        <Redirect
                            to={{
                                pathname: pathname,
                                state: { from: props.location }
                            }}
                        />
                    );
                }

                // Signed in, but signed in as WHOM. isLoggedIn is 'true' for
                // every account there is, customers included, so on its own it
                // let a customer walk into /admin on a stale bookmark or a
                // pasted link. Refusing them is right; what happened next was
                // not. The refusal route ends at /admin/login, and that page
                // calls logout() for any account whose clientname is not 'dme'
                // (pages/admin/AdminViews/Login.js) - correct when it is
                // rejecting wrong credentials at the admin sign-in box, and
                // ruinous when the visitor is a signed-in customer, who loses
                // the token they arrived with and the session they were in the
                // middle of. So a customer is sent to their own home instead,
                // and nothing in localStorage is touched on the way.
                //
                // /customerdashboard/* is guarded by this same component and is
                // where customers belong, so only /admin/* is gated on staff.
                const isAdminArea = props.location.pathname.indexOf('/admin') === 0;

                // Staff is `accountType`, not `clientname`. clientname
                // is a company NAME, and the identity endpoint answers 'dme'
                // both for DME staff and for a client record that happens to be
                // named "dme". account_type is decided by the server from
                // dme_employees, so 'dme' there means staff and any other
                // answer ('client', 'unlinked') is sent home. The admin sign-in
                // never lets an unlinked login in either (AdminViews/Login.js).
                //
                // Both arrive from getUser(), which Header dispatches on mount,
                // so on a cold load they are briefly unknown. Unknown is NOT
                // treated as "not staff": that would bounce a genuine DME user
                // off /admin every time they refreshed the page. If the call
                // fails after an answer, the type is dropped (FAILED_GET_USER)
                // and the name that is left still sends a client home, as it
                // did before. Each endpoint makes its own decision on the server.
                const notStaff = accountType != null
                    ? accountType !== 'dme'
                    : Boolean(clientname) && clientname !== 'dme';
                if (isAdminArea && notStaff) {
                    return (
                        <Redirect
                            to={{
                                pathname: '/',
                                state: { from: props.location }
                            }}
                        />
                    );
                }

                return <Component {...props} />;
            }}
        />
    );
}

AdminPrivateRoute.propTypes = {
    location: PropTypes.object,
    component: PropTypes.func.isRequired,
    clientname: PropTypes.string,
    accountType: PropTypes.string,
};

const isLogged = () => {
    return localStorage.getItem('isLoggedIn');
};

const mapStateToProps = (state) => {
    return {
        clientname: state.auth.clientname,
        accountType: state.auth.accountType,
    };
};

// withRouter OUTSIDE connect, and it is load-bearing rather than decorative.
//
// connect() gives this component a shouldComponentUpdate that returns false
// unless its own props shallow-differ or the store state changes. Navigating
// does neither. These elements are children of <Main>, NOT of the <Switch>
// (AppRouter.js), and <Main> passes them down through PageWrapper's
// React.Children.map(cloneElement(child)) with no new props, so they stay
// shallow-equal on a location change. A false from shouldComponentUpdate means
// the nested <Route> never re-renders, never recomputes `match`, and the admin
// page under the old URL stays on screen while the sidebar highlight moves.
//
// That would break EVERY page under /admin and /customerdashboard, not just the
// Fr8Beacon ones. It did not bite before this component was connected, because
// a plain function component has no shouldComponentUpdate at all.
//
// withRouter renders a <Route render={...}>, which hands down a fresh location
// on every navigation, so the shallow compare sees a real change. That is why
// PrivateRoute gets away without it: its elements ARE direct children of
// <Switch>, and Switch clones the match with {location, computedMatch}.
const ConnectedAdminPrivateRoute = withRouter(
    connect(mapStateToProps)(AdminPrivateRoute)
);

export {
    ConnectedAdminPrivateRoute as AdminPrivateRoute
};



// WEBPACK FOOTER //
// src/shared/AdminPrivateRoute/AdminPrivateRoute.js